/**
 * 出图任务的**槽位表**与**相位** —— 等待态重做的地基(W1,2026-08-02)。
 *
 * ═══ 为什么要有这个东西 ═══════════════════════════════════════════════════════
 * 「这一单会出几张、每一张将来是什么、现在到哪了」这三个问题,改前散在五处各答各的:
 *   `detectShape` 认形态、`pendingRatios` 猜还差几张、`captionOf` 算卡底副标题、
 *   `stageBadge` 算主画面角标、页头模板自己拼「n/N 成功」。
 * 于是同一屏能出现互相矛盾的两句话 —— 真跑坐实过一次:页头写「2/6 成功」,
 * 下面六个骨架全在转(因为 assets 压根没拉),续出条还说「封面还没出好」。
 *
 * 这里把「槽位」定义成唯一真源:**一个槽 = 一张将来会有的图**,它从第一秒就存在,
 * 有自己的 seq、目标画幅和身份;产物到货只是把槽从 waiting 翻成 landed/failed。
 *
 * ═══ 三条口径纪律 ═════════════════════════════════════════════════════════════
 * ① **只用后端真给的字段**:requestedCount / groupPointTitles / ratios / ratio /
 *    每张 asset 的 seq 与 status。一个都不新造,更不猜「预计还要多久」
 *    (`estimatedSecondsRemaining` 2026-07-16 已从 DTO 删除,写出来就是编的)。
 * ② **不指名「哪一张正在画」**。并行 fan-out 时同时在画好几张,后端也不告诉前端
 *    哪张在机器上 —— 所以槽只有 landed / failed / waiting 三态,没有 running。
 *    指名一张 = 说了一句我们不知道的话。
 * ③ **槽位按 seq 对齐,不按到货顺序**。老 `pendingRatios` 按「已落地 N 个 → 取 ratios[N]」
 *    推缺哪一张,只要有一张先失败或后到就会取错画幅(单测坐实:seq1 成功 + seq3 失败时,
 *    缺的是 seq2 的 1:1,它给出 16:9)。占位框按错画幅撑开,图回来时宽度突变。
 */
import { CHANNEL_GROUPS } from "./free-create/channelPresets";
import { nearestRatio } from "./free-create/nearestRatio";
import type { PosterAssetDto, PosterTaskDto } from "../api/posterService";

/** 页面相位 —— 等待态是一等状态,不是「成品页减内容」。 */
export type GenPhase = "UNAVAILABLE" | "QUEUED" | "STREAMING" | "SETTLED";

/** 槽位三态。★没有 running:见文件头纪律 ②。 */
export type SlotState = "landed" | "failed" | "waiting";

/**
 * 槽的**语义类别** —— 让槽位表自描述,消费端不用再回头问 task 是什么形态。
 * 收口前 `captionOf` / `stageBadge` 各自拿 (shape, task) 重算一遍身份,同一件事三处口径。
 */
export type SlotKind = "group" | "channel" | "plain";

export interface GenSlot {
  /** 1-based,与后端 asset.seq 同一口径 */
  seq: number;
  kind: SlotKind;
  /** 这一槽的**目标**画幅(套图逐槽不同) */
  ratio: string;
  /** 这一格将来是什么 —— 组图=爆点标题,套图=渠道名,其余=第 N 张 */
  label: string;
  /** 副标题(封面标记 / 比例);没有就不显示,不塞占位字 */
  sub?: string;
  state: SlotState;
  /** 已落地时挂上原始产物(同 seq 多条取最新那条) */
  asset?: PosterAssetDto;
  /** 超出台本张数的额外槽(续出 / 重出产生) */
  extra?: boolean;
  /**
   * 分步出图的「待续出」槽(2026-08-03):台本里有它,但**这一批没下单**
   * (先出封面:requestedCount=1 < 台本张数)。它不在「正在出」的行列 ——
   * 说「还差 4 张正在出」而其实只出 1 张,就是等待页在说谎。
   */
  deferred?: boolean;
}

export interface GenerationProgress {
  phase: GenPhase;
  /** 恒按 seq 升序 —— 爆点①②③ 的顺序本身有语义,倒排会让用户对不上号 */
  slots: GenSlot[];
  total: number;
  done: number;
  failed: number;
  /** 本批真正在出的waiting(不含 deferred)。 */
  waiting: number;
  /** 待续出(分步出图,本批没下单)。 */
  deferred: number;
}

type TaskLike = Pick<
  PosterTaskDto,
  "status" | "posterKind" | "ratio" | "ratios" | "groupPointTitles" | "requestedCount"
>;

const EMPTY: GenerationProgress = {
  phase: "QUEUED",
  slots: [],
  total: 0,
  done: 0,
  failed: 0,
  waiting: 0,
  deferred: 0,
};

/** 还在出图途中(只有这两个状态算「在跑」,与详情页取消按钮同一判据)。 */
function inFlight(status?: string | null): boolean {
  return status === "PENDING" || status === "RUNNING";
}

function isGroup(task: TaskLike): boolean {
  return task.posterKind === "GROUP" || (task.groupPointTitles?.length ?? 0) > 0;
}

function isRatioSet(task: TaskLike): boolean {
  return task.posterKind === "RATIO_SET" || (task.ratios?.length ?? 0) >= 2;
}

/** 台本张数 —— 组图认台本、套图认比例数、其余认 requestedCount。 */
function plannedTotal(task: TaskLike): number {
  if (isGroup(task) && (task.groupPointTitles?.length ?? 0) > 0) {
    return task.groupPointTitles!.length;
  }
  if (isRatioSet(task) && (task.ratios?.length ?? 0) > 0) return task.ratios!.length;
  return task.requestedCount ?? 0;
}

/**
 * 套图槽位的身份用**渠道名**而不是比例 —— 用户心智是「我要发抖音」,不是「我要 9:16」
 * (channelPresets 文件头的同一条理由)。认不出的比例回落成比例本身,不给空标题。
 */
function channelLabel(ratio: string): string {
  return CHANNEL_GROUPS.find((g) => g.ratio === ratio)?.label ?? ratio;
}

function slotIdentity(
  task: TaskLike,
  seq: number,
  ratio: string
): { kind: SlotKind; label: string; sub?: string } {
  if (isGroup(task)) {
    const title = task.groupPointTitles?.[seq - 1];
    return {
      kind: "group",
      label: title || `第 ${seq} 张`,
      // 封面是组图分步的版式锚(后续张照着它出),值得单独标一下;其余张不加噪音
      sub: seq === 1 ? "封面" : undefined,
    };
  }
  if (isRatioSet(task)) return { kind: "channel", label: channelLabel(ratio), sub: ratio };
  return { kind: "plain", label: `第 ${seq} 张`, sub: ratio };
}

function slotRatio(task: TaskLike, seq: number): string {
  if (isRatioSet(task)) return task.ratios?.[seq - 1] ?? task.ratio ?? "3:4";
  return task.ratio ?? "3:4";
}

/**
 * 槽位表 + 相位。
 *
 * @param task        当前任务(还没到货传 null)
 * @param assets      已落地的产物(调用方应传**展示集**:长图分段已被 displayableAssets 排除)
 * @param unavailable 轮询判定的「打不开」(404/403);压过一切
 */
export function generationProgress(
  task: TaskLike | null,
  assets: PosterAssetDto[],
  unavailable = false
): GenerationProgress {
  if (unavailable) return { ...EMPTY, phase: "UNAVAILABLE" };
  if (!task) return EMPTY;

  // 同一 seq 可能有多条(改图/重画是软删旧行 + 插新行,产生新 id)→ 取 id 最大的那条。
  // 用 max(id) 而不是「优先 SUCCESS」:重出成功与重出又失败都要如实反映最后一次结果。
  const bySeq = new Map<number, PosterAssetDto>();
  for (const a of assets) {
    const prev = bySeq.get(a.seq);
    if (!prev || a.id > prev.id) bySeq.set(a.seq, a);
  }

  const planned = plannedTotal(task);
  const maxSeq = bySeq.size ? Math.max(...bySeq.keys()) : 0;
  const settled = !inFlight(task.status);

  // 槽位数 = max(台本张数, 已到货的最大 seq)。
  // ★取 max 而不是取台本:续出/重出会让 seq 超出台本,少一格就是**吞掉一张已经扣过钱的图**。
  const count = Math.max(planned, maxSeq);

  // 分步出图(B2-1 先出封面):本批下单张数 = requestedCount;台本里超出它的槽是
  // 「待续出」,不算「正在出」。只对组图生效(其余形态 requestedCount 就是全部)。
  const activeBatch =
    isGroup(task) && (task.requestedCount ?? 0) > 0 ? task.requestedCount! : planned;
  const slots: GenSlot[] = [];
  for (let seq = 1; seq <= count; seq++) {
    const asset = bySeq.get(seq);
    // ★终态下没到货的**本批**槽直接不出现:任务已经结束了还挂着「还没回来」的空格,等不到的。
    //   (真跑抓到过:长图整图没交付时,老代码在终态还渲一排转圈的骨架)
    //   待续出槽终态**保留**:封面出完任务就是 SUCCESS,后面几张不是「等不到」,
    //   是「等你点续出」—— 藏掉它们,续出条说的「剩下 N 张」就没了对照物。
    const deferred = !asset && seq > activeBatch && seq <= planned;
    if (settled && !asset && !deferred) continue;
    const ratio = slotRatio(task, seq);
    const { kind, label, sub } = slotIdentity(task, seq, ratio);
    slots.push({
      seq,
      kind,
      ratio,
      label,
      sub: deferred ? "待续出" : sub,
      state: asset ? (asset.status === "FAILED" ? "failed" : "landed") : "waiting",
      asset,
      ...(seq > planned ? { extra: true } : {}),
      ...(deferred ? { deferred: true } : {}),
    });
  }

  const done = slots.filter((s) => s.state === "landed").length;
  const failed = slots.filter((s) => s.state === "failed").length;
  const deferredCount = slots.filter((s) => s.deferred).length;
  const waiting = slots.filter((s) => s.state === "waiting" && !s.deferred).length;

  const phase: GenPhase = settled ? "SETTLED" : done + failed === 0 ? "QUEUED" : "STREAMING";

  return {
    phase,
    slots,
    total: Math.max(planned, slots.length),
    done,
    failed,
    waiting,
    deferred: deferredCount,
  };
}

/**
 * 成品卡底副标题 —— 从槽位表派生,不再自己回头问 task 是什么形态(收口,2026-08-02)。
 *
 * ★套图这一支保留了一条**刻意的差异**:副标题用**真实像素反查**出来的比例,
 *   而不是槽位上的目标比例。上游中转不一定分毫不差照做 size(实测皮皮虾把
 *   1024x1408 归一成 864x1152)—— 卡上要标用户**真正拿到**的那个,
 *   而槽位的 `ratio` 是「我们要的」。两个数不是一回事,收口时不能顺手合并。
 */
export function slotCaption(
  slot: GenSlot | undefined,
  asset: Pick<PosterAssetDto, "actualWidth" | "actualHeight">
): string | undefined {
  if (!slot) return undefined;
  if (slot.kind === "group") return slot.label;
  if (slot.kind === "channel") {
    if (asset.actualWidth && asset.actualHeight) {
      const r = nearestRatio(asset.actualWidth, asset.actualHeight);
      if (r) return r;
    }
    return slot.ratio;
  }
  return undefined;
}

/** 主画面角标(组图:当前这张在讲哪个爆点)。 */
export function slotStageBadge(slot: GenSlot | undefined): string | undefined {
  if (!slot || slot.kind !== "group") return undefined;
  return `${slot.seq}. ${slot.label}`;
}

/** seq → 槽,消费端按 asset.seq 直接取。 */
export function slotBySeq(slots: GenSlot[]): Map<number, GenSlot> {
  return new Map(slots.map((s) => [s.seq, s]));
}
